import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Capacitor } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';

import { NativeAuthFlag, readNativeAuthFlag } from './nativeAuthFlag';
import {
    NativeAuthAvailability,
    NativeAuthMethod,
    NativeAuthTenant,
    nativeGetSession,
    readNativeAuthAvailability,
} from 'src/native/nativeAuth';

interface NativeAuthAvailabilityState {
    /** Undefined until the shell has answered, so nothing renders against a guessed owner. */
    isNativeAuthAvailable: boolean | undefined;
    tenant: NativeAuthTenant | undefined;
    /** Which flows the shell handles. Anything not listed stays on the web app's own flow. */
    methods: NativeAuthMethod[];
}

interface NativeAuthAvailabilityContextValue extends NativeAuthAvailabilityState {
    /** Asks the shell again. Call before a login, since the shell's toggle can flip while signed out. */
    refresh: () => void;
}

/** What this web app reports when it has declined native auth, with no bridge call to find out. */
const DECLINED: NativeAuthAvailabilityState = {
    isNativeAuthAvailable: false,
    tenant: undefined,
    methods: [],
};

const NativeAuthAvailabilityContext = createContext<NativeAuthAvailabilityContextValue>({
    ...DECLINED,
    refresh: () => undefined,
});

const PENDING: NativeAuthAvailabilityState = {
    isNativeAuthAvailable: undefined,
    tenant: undefined,
    methods: [],
};

function toNativeAuthState(
    availability: NativeAuthAvailability | undefined
): NativeAuthAvailabilityState {
    const isNativeAuthAvailable = availability?.available ?? false;
    return {
        isNativeAuthAvailable,
        tenant: availability?.tenant,
        methods: isNativeAuthAvailable ? availability?.methods ?? [] : [],
    };
}

type ShellSession = 'authenticated' | 'signedOut' | 'unreadable';

const readShellSession = (): Promise<ShellSession> =>
    nativeGetSession().then(
        (session) => (session.authenticated ? 'authenticated' : 'signedOut'),
        () => 'unreadable'
    );

async function readNativeAuthState(flag: NativeAuthFlag): Promise<NativeAuthAvailabilityState> {
    if (flag === 'off') return DECLINED;
    const state = toNativeAuthState(await readNativeAuthAvailability());
    if (flag === 'on' || !state.isNativeAuthAvailable) return state;
    // No flag answer: an existing shell session keeps its owner, but no new login goes to the shell.
    // An unreadable session may still exist, and useNativeAuth retries the read once back online.
    const shellSession = await readShellSession();
    datadogRum.addAction('Native Auth Flag Unknown', { shellSession });
    return shellSession === 'signedOut' ? DECLINED : { ...state, methods: [] };
}

// Read at boot and again before each login: ownership only moves while signed out, where no session
// exists for the two sides to disagree about.
export function NativeAuthAvailabilityProvider({ children }: { children: ReactNode }) {
    // Declined synchronously outside the shell, so a browser never waits on the flag. Inside it,
    // pending until the flag and the shell answer, so no consumer sees a guessed owner.
    const [nativeAuthState, setNativeAuthState] = useState<NativeAuthAvailabilityState>(
        Capacitor.isNativePlatform() ? PENDING : DECLINED
    );

    // Only the newest read may land, so a slow answer cannot overwrite a fresher one.
    const latestRead = useRef(0);

    const refresh = useCallback(() => {
        if (!Capacitor.isNativePlatform()) return;
        const read = ++latestRead.current;
        readNativeAuthFlag()
            .then(readNativeAuthState)
            .then((next) => {
                if (read === latestRead.current) setNativeAuthState(next);
            });
    }, []);

    const value = useMemo(() => ({ ...nativeAuthState, refresh }), [nativeAuthState, refresh]);

    useEffect(() => {
        refresh();
        return () => {
            latestRead.current += 1;
        };
    }, [refresh]);

    return (
        <NativeAuthAvailabilityContext.Provider value={value}>
            {children}
        </NativeAuthAvailabilityContext.Provider>
    );
}

export function useNativeAuthAvailability(): NativeAuthAvailabilityContextValue {
    return useContext(NativeAuthAvailabilityContext);
}
